export enum StripeProductPriceEnum {
  SERVICE_FEE_ID = 'service-fee',
  ONE_TIME_FEE_ID = 'one_time',
  SINGLE_HYDROTEST_PRODUCT_ID = 'original-hydro-price-per-test',
}

/**
 * Minimal structural view of a Stripe Price as the catalog returns it —
 * enough to identify a price without importing the server-side `stripe`
 * package into ui-core.
 */
export interface StripePriceRefI {
  id: string;
  lookup_key?: string | null;
}

/** A price "is" one of the legacy named prices when the constant matches its
 * id (legacy plan-style prices carry custom ids) OR its lookup_key (modern
 * dashboard-created prices always have Stripe-generated `price_…` ids, and can
 * only carry the legacy name as a lookup key). Matching on id alone was the
 * #2015 defect: it never matched a live price, so productDefaultPriceId was
 * silently empty on every quote and charge. */
export function matchesStripePriceKey(price: StripePriceRefI, key: StripeProductPriceEnum): boolean {
  return price.id === key || price.lookup_key === key;
}

/**
 * Resolve a product's pinned default price id for the checkout funnel: the
 * single-hydro-test price when the product carries it (by id or lookup_key),
 * else '' — meaning "let the server resolve it" (product default_price, else
 * first active price). Shared by the quote (calculateTotal) and the charge
 * (createPaymentIntent) so both always send the identical price identity
 * (#2001/#2015).
 */
export function resolveProductDefaultPriceId(prices: readonly StripePriceRefI[] | null | undefined): string {
  const pinned = prices?.find(price =>
    matchesStripePriceKey(price, StripeProductPriceEnum.SINGLE_HYDROTEST_PRODUCT_ID),
  );
  return pinned?.id ?? '';
}
